import React from 'react';
import { X, ShieldAlert, Zap, UserCheck } from 'lucide-react';
import { formatCurrencyBRL } from '../utils/formatUtils';

interface DemoPdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleteSignup: () => void;
  formData: any;
  finalPrice: number;
}

export default function DemoPdfPreviewModal({
  isOpen,
  onClose,
  onCompleteSignup,
  formData,
  finalPrice,
}: DemoPdfPreviewModalProps) {
  if (!isOpen) return null;

  const todayStr = new Date().toLocaleDateString('pt-BR');
  const clientName = formData?.clientName?.trim() || '[NOME DO CLIENTE]';
  const serviceName = formData?.serviceName?.trim() || '[NOME DO SERVIÇO / PRODUTO]';
  const products = formData?.products || [];
  const services = formData?.services || [];

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-zinc-950/70 backdrop-blur-sm p-4 overflow-y-auto font-sans">
      <div className="relative w-full max-w-4xl bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl flex flex-col my-8 overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-brand" />
            <h2 className="text-base font-bold text-white uppercase tracking-wider">
              Visualização de Demonstração
            </h2>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 text-zinc-400 hover:text-white hover:bg-zinc-850 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content - Two Columns */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-6 max-h-[75vh]">
          
          {/* Left Column: Simulated PDF Sheet */}
          <div className="flex-1 bg-white border border-zinc-200 shadow-lg rounded-xl p-8 relative overflow-hidden text-zinc-900 min-h-150 flex flex-col justify-between">
            
            {/* Watermark Diagonal */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.03]">
              <span className="text-zinc-900 font-black text-5xl uppercase tracking-[0.2em] rotate-[-35deg] text-center whitespace-pre-line leading-relaxed">
                ORÇAMENTO DE{"\n"}DEMONSTRAÇÃO
              </span>
            </div>

            {/* Top Sheet Notice Banner */}
            <div className="absolute top-0 left-0 right-0 bg-zinc-100 border-b border-zinc-200 py-1.5 px-4 text-center select-none shrink-0 flex items-center justify-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
                Modelo de Demonstração - Marca Não Configurada
              </span>
            </div>

            {/* Simulated PDF Header */}
            <div className="mt-6 flex justify-between items-start border-b border-zinc-100 pb-6 shrink-0">
              <div className="text-left space-y-2">
                {/* Logo Placeholder */}
                <div className="w-16 h-16 border-2 border-dashed border-zinc-300 rounded-xl flex items-center justify-center bg-zinc-50 select-none">
                  <span className="text-[8px] font-bold text-zinc-400 text-center px-1 leading-tight">
                    [SUA LOGOMARCA AQUI]
                  </span>
                </div>
                
                <h3 className="text-lg font-black text-zinc-950 tracking-tight uppercase">
                  [NOME DA SUA EMPRESA]
                </h3>
                <p className="text-xs text-zinc-500 font-bold">
                  [SEU TELEFONE] • [SEU ENDEREÇO]
                </p>
              </div>

              <div className="text-right space-y-3 min-w-30">
                <div>
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider margin-0">NÚMERO</p>
                  <p className="text-base font-black text-zinc-950 margin-0">0001</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider margin-0">DATA</p>
                  <p className="text-base font-black text-zinc-950 margin-0">{todayStr}</p>
                </div>
              </div>
            </div>

            {/* Recipient Details */}
            <div className="my-6 text-left shrink-0">
              <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider mb-1">PROPOSTA PARA</p>
              <p className="text-xl font-black text-zinc-950">{clientName}</p>
            </div>

            {/* Budget Items Table */}
            <div className="flex-1 py-4 border-t border-zinc-200">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-900">
                    <th className="py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Descrição</th>
                    <th className="py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-wider text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 && services.length === 0 ? (
                    <tr className="border-b border-zinc-100">
                      <td className="py-4 pr-4">
                        <div className="font-bold text-zinc-900 text-sm">{serviceName}</div>
                        <div className="text-[10px] text-zinc-400 mt-0.5">Execução e viabilização técnica do projeto.</div>
                      </td>
                      <td className="py-4 text-right font-black text-zinc-900 text-sm">
                        {formatCurrencyBRL(finalPrice)}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {products.map((p: any, idx: number) => (
                        <tr key={`p-${idx}`} className="border-b border-zinc-100 text-xs">
                          <td className="py-3 pr-4">
                            <div className="font-bold text-zinc-900">{p.name || 'Item de Material'}</div>
                            <div className="text-[9px] text-zinc-400 mt-0.5">
                              Quantidade: {p.quantity || 1} • {p.isMarkupHidden ? 'Insumo Incluso' : 'Componente Comercializado'}
                            </div>
                          </td>
                          <td className="py-3 text-right font-bold text-zinc-900">
                            {formatCurrencyBRL(p.isMarkupHidden ? (p.total || 0) : (p.total || 0) * (finalPrice / (products.reduce((acc: number, item: any) => acc + (Number(item.total) || 0), 0) + services.reduce((acc: number, item: any) => acc + (Number(item.value) || 0), 0) || 1)))}
                          </td>
                        </tr>
                      ))}
                      {services.map((s: any, idx: number) => (
                        <tr key={`s-${idx}`} className="border-b border-zinc-100 text-xs">
                          <td className="py-3 pr-4">
                            <div className="font-bold text-zinc-900">{s.name || 'Mão de Obra'}</div>
                            <div className="text-[9px] text-zinc-400 mt-0.5">Serviço de Execução</div>
                          </td>
                          <td className="py-3 text-right font-bold text-zinc-900">
                            {formatCurrencyBRL(s.value || 0)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Simulated PDF Footer */}
            <div className="border-t border-zinc-200 pt-4 mt-6 text-left space-y-4 shrink-0">
              <div className="flex justify-between items-center bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Valor do Investimento</span>
                <span className="text-lg font-black text-zinc-950">{formatCurrencyBRL(finalPrice)}</span>
              </div>
              <div className="text-[10px] text-zinc-500 space-y-1 font-semibold leading-tight">
                <p>Validade: 15 dias após a emissão.</p>
                <p>Condições de Pagamento: [SUAS OBSERVAÇÕES E CONDIÇÕES DE PAGAMENTO]</p>
              </div>
              <div className="border-t border-zinc-100 pt-3 flex justify-between items-center text-[9px] font-bold text-zinc-400 uppercase tracking-wider">
                <span>Assinado por: [SEU NOME]</span>
                <span>Powered by R-MAX</span>
              </div>
            </div>

          </div>

          {/* Right Column: CTA Conversion Panel */}
          <div className="w-full lg:w-[320px] shrink-0 flex flex-col justify-center text-center lg:text-left space-y-6">
            <div className="space-y-3 bg-zinc-850 p-6 rounded-2xl border border-zinc-800">
              <div className="bg-brand/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto lg:mx-0">
                <Zap className="h-6 w-6 text-brand" />
              </div>
              
              <h4 className="text-lg font-black text-white leading-tight uppercase tracking-tight">
                Gostou do resultado?
              </h4>
              
              <p className="text-zinc-400 text-xs leading-relaxed font-medium">
                Esta é uma demonstração do orçamento profissional que você pode enviar ao seu cliente.
              </p>
              
              <div className="pt-2 text-zinc-400 text-xs space-y-2 text-left font-semibold">
                <div className="flex items-start gap-2">
                  <span className="text-brand font-bold text-sm shrink-0">✓</span>
                  <span>Insira sua logomarca oficial</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-brand font-bold text-sm shrink-0">✓</span>
                  <span>Personalize o nome da sua empresa, telefone e chave PIX</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-brand font-bold text-sm shrink-0">✓</span>
                  <span>Envie direto via WhatsApp com um clique</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={onCompleteSignup}
                className="w-full h-14 bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-[0.98] shadow-lg shadow-blue-500/20 group cursor-pointer"
              >
                <UserCheck className="h-5 w-5 group-hover:scale-110 transition-transform" />
                <span>Completar meu Cadastro</span>
              </button>
              
              <button
                type="button"
                onClick={onClose}
                className="w-full py-3 text-zinc-400 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors"
              >
                Fechar Visualização
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
